<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Settings History</span><br/>
      <span style="font-style: italic; font-size: 18px">history of settings updates</span>
    </div>

    <div style="font-size: 22px; font-weight: bold; position: relative; margin: 30px" class="d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      &nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;Filter Results&nbsp;<b-form-input style="width: 260px" v-model="text_filter" placeholder=""/>
    </div>

    <div v-if="!loading">
      <div class="row" style="margin: 30px 10px 30px 10px; font-weight: bold; font-size: 22px;">
        <div class="col" style="border: 1px solid grey;">Total: {{items_full.length}}</div>
        <div class="col" style="border: 1px solid grey;">Filtered: {{items.length}}</div>
      </div>
    </div>

    <div>
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall}}
        </template>

        <template slot="updated_by" slot-scope="row">
          <div>{{ format_updated_by(row.item.updated_by) }}</div>
          <div v-if="display_name_differs(row.item.updated_by)" class="updated-by-username">
            {{ row.item.updated_by }}
          </div>
        </template>

        <template slot="diff" slot-scope="row">
          <div class="diff-cell">
            <!-- Friendly summary header -->
            <div v-if="get_event_message(row.item)" class="diff-event-msg">
              <span class="diff-badge badge-event">Event</span>
              <span>{{ get_event_message(row.item) }}</span>
            </div>

            <div v-else-if="get_changes(row.item).length === 0" class="diff-empty">
              <em>No tracked field changes</em>
            </div>

            <div v-else>
              <div class="diff-summary">
                {{ get_changes(row.item).length }} field{{ get_changes(row.item).length === 1 ? '' : 's' }} changed
              </div>
              <div
                v-for="(change, idx) in visible_changes(row.item)"
                :key="idx"
                class="diff-row"
                :class="'diff-' + change.type"
              >
                <span class="diff-badge" :class="'badge-' + change.type">
                  {{ badge_label(change.type) }}
                </span>
                <span class="diff-field" :title="change.path">{{ humanize_path(change.path) }}</span>
                <div v-if="change.type === 'changed'" class="diff-values">
                  <div class="diff-old"><span class="diff-arrow">−</span> {{ format_value(change.old) }}</div>
                  <div class="diff-new"><span class="diff-arrow">+</span> {{ format_value(change.new) }}</div>
                </div>
                <div v-else-if="change.type === 'added'" class="diff-values">
                  <div class="diff-new"><span class="diff-arrow">+</span> {{ format_value(change.new) }}</div>
                </div>
                <div v-else-if="change.type === 'removed'" class="diff-values">
                  <div class="diff-old"><span class="diff-arrow">−</span> {{ format_value(change.old) }}</div>
                </div>
              </div>
              <div v-if="get_changes(row.item).length > visible_limit && !is_expanded(row.item)" class="diff-more">
                <a href="#" v-on:click.prevent="toggle_expanded(row.item)">
                  Show {{ get_changes(row.item).length - visible_limit }} more change{{ (get_changes(row.item).length - visible_limit) === 1 ? '' : 's' }}
                </a>
              </div>
              <div v-else-if="get_changes(row.item).length > visible_limit" class="diff-more">
                <a href="#" v-on:click.prevent="toggle_expanded(row.item)">Show fewer</a>
              </div>
            </div>

            <div class="diff-raw-toggle">
              <a href="#" v-on:click.prevent="toggle_raw(row.item)">
                {{ is_raw_visible(row.item) ? 'Hide raw JSON' : 'Show raw JSON' }}
              </a>
            </div>
            <div v-if="is_raw_visible(row.item)" class="diff-raw">
              <div class="diff-raw-section">
                <div class="diff-raw-label">Raw diff</div>
                <pre>{{ row.item.diff }}</pre>
              </div>
              <div v-if="row.item.old" class="diff-raw-section">
                <div class="diff-raw-label">Before</div>
                <pre>{{ pretty_json(row.item.old) }}</pre>
              </div>
              <div v-if="row.item.new" class="diff-raw-section">
                <div class="diff-raw-label">After</div>
                <pre>{{ pretty_json(row.item.new) }}</pre>
              </div>
            </div>
          </div>
        </template>

      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

import {get_booking_file_history, get_tenant_config_history, get_users} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import {justDate} from "@/helpers/dates";

export default {
  name: 'tenant_config_history',
  components: {
  },

  data() {
    return {
      text_filter: '',
      items: [],
      items_full: [],
      summary: [],
      loading: true,
      details_fromDate: justDate(moment().add(-7, 'days')),
      details_toDate: justDate(moment()),
      booking_type: '',
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pages: [],
      fields_all: [],
      fields: [
        { key: 'processed_time', sortable: true, label: 'When', thStyle: { width: "150px" }},
        { key: 'updated_by', sortable: true, label: 'Updated by', thStyle: { width: "180px" }},
        { key: 'diff', sortable: false, label: 'Changes'},
      ],
      visible_limit: 8,
      expanded_rows: {},
      raw_visible_rows: {},
      changes_cache: {},
      users_by_username: {},
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment().add(0, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },

    }
  },

  async created() {
    this.fields_all = this.fields
    this.check_features()
  },
  methods: {
    async check_features() {
      this.load_data()
    },
    async load_data() {
      this.loading = true
      this.changes_cache = {}
      this.expanded_rows = {}
      this.raw_visible_rows = {}
      try {
        // Load users in parallel; failure here is non-fatal because the
        // history table will still render with raw usernames.
        const history_promise = get_tenant_config_history(
          this.user.tenant,
          moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
        )
        const users_promise = get_users(this.user.tenant).catch(() => null)

        const [history_resp, users_resp] = await Promise.all([history_promise, users_promise])
        this.users_by_username = this.build_users_map(users_resp)

        const {value: { data = [] } } = history_resp
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.records
        this.items_full = data.records
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    filter_results() {
      this.items = this.items_full
      if (this.text_filter != '') {
        const val = this.text_filter
        this.items = this.items.filter(item =>
          this.contains(JSON.stringify(item.diff), val) ||
          this.contains(item.updated_by, val) ||
          this.contains(this.format_updated_by(item.updated_by), val) ||
          this.contains(JSON.stringify(this.get_changes(item)), val)
        )
      }
    },

    build_users_map(users_resp) {
      const map = {}
      try {
        const results = users_resp && users_resp.value && users_resp.value.data
          ? (users_resp.value.data.results || [])
          : []
        for (const u of results) {
          if (u && u.username) {
            map[u.username.toLowerCase()] = u
          }
        }
      } catch (e) {
        // best-effort lookup; fall back to username if anything goes wrong
      }
      return map
    },
    format_updated_by(username) {
      if (!username) return ''
      const user = this.users_by_username[String(username).toLowerCase()]
      if (!user) return username
      const first = (user.first_name || '').trim()
      const last = (user.last_name || '').trim()
      const full = [first, last].filter(Boolean).join(' ')
      return full || username
    },
    display_name_differs(username) {
      if (!username) return false
      const formatted = this.format_updated_by(username)
      return formatted && formatted !== username
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },

    row_key(item) {
      // processed_time is unique per record
      return item.processed_time + '|' + (item.updated_by || '')
    },
    is_expanded(item) {
      return !!this.expanded_rows[this.row_key(item)]
    },
    toggle_expanded(item) {
      const key = this.row_key(item)
      this.$set(this.expanded_rows, key, !this.expanded_rows[key])
    },
    is_raw_visible(item) {
      return !!this.raw_visible_rows[this.row_key(item)]
    },
    toggle_raw(item) {
      const key = this.row_key(item)
      this.$set(this.raw_visible_rows, key, !this.raw_visible_rows[key])
    },

    /**
     * For records produced by yard/event add/delete handlers, the API stores
     * a human-readable summary string in `diff` (e.g. "New Yard Added: ...")
     * while `old` and `new` are identical references. Detect these so we can
     * show the friendly message instead of trying to deep-diff identical
     * snapshots.
     */
    get_event_message(item) {
      if (!item || !item.diff) return null
      if (this.has_real_old_new_diff(item)) return null
      const diff_str = String(item.diff)
      const event_prefixes = [
        'New Yard Added:',
        'Yard Deleted:',
        'New HOS Event Added:',
        'HOS Event Deleted:',
        'New Vehicle Late Alert Event Added:',
        'Vehicle Late Alert Event Deleted:',
        'New Booking State Alert Event Added:',
        'Booking State Alert Event Deleted:',
        'New Cust PreTrip Alert Event Added:',
        'Updated Cust PreTrip Alert Event',
        'Deleted Cust PreTrip Alert Event:',
      ]
      for (const prefix of event_prefixes) {
        if (diff_str.startsWith(prefix)) {
          return diff_str
        }
      }
      return null
    },
    has_real_old_new_diff(item) {
      if (!item || item.old == null || item.new == null) return false
      try {
        return JSON.stringify(item.old) !== JSON.stringify(item.new)
      } catch (e) {
        return false
      }
    },

    get_changes(item) {
      const key = this.row_key(item)
      if (this.changes_cache[key]) return this.changes_cache[key]
      let changes = []
      if (this.has_real_old_new_diff(item)) {
        changes = this.deep_diff(item.old, item.new, '')
      }
      this.changes_cache[key] = changes
      return changes
    },
    visible_changes(item) {
      const all = this.get_changes(item)
      if (this.is_expanded(item) || all.length <= this.visible_limit) return all
      return all.slice(0, this.visible_limit)
    },

    deep_diff(oldVal, newVal, path) {
      const changes = []
      this._deep_diff_into(oldVal, newVal, path, changes)
      return changes
    },
    _deep_diff_into(oldVal, newVal, path, changes) {
      if (this.values_equal(oldVal, newVal)) return

      const oldIsObj = this.is_plain_object(oldVal)
      const newIsObj = this.is_plain_object(newVal)
      const oldIsArr = Array.isArray(oldVal)
      const newIsArr = Array.isArray(newVal)

      if (oldIsObj && newIsObj) {
        const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)])
        const sorted = [...keys].sort()
        for (const k of sorted) {
          const sub_path = path ? `${path}.${k}` : k
          const old_has = Object.prototype.hasOwnProperty.call(oldVal, k)
          const new_has = Object.prototype.hasOwnProperty.call(newVal, k)
          if (old_has && new_has) {
            this._deep_diff_into(oldVal[k], newVal[k], sub_path, changes)
          } else if (new_has) {
            changes.push({ type: 'added', path: sub_path, new: newVal[k] })
          } else {
            changes.push({ type: 'removed', path: sub_path, old: oldVal[k] })
          }
        }
        return
      }

      if (oldIsArr && newIsArr) {
        const id_key = this.detect_array_id_key(oldVal, newVal)
        if (id_key) {
          this._diff_arrays_by_id(oldVal, newVal, path, id_key, changes)
        } else {
          const max_len = Math.max(oldVal.length, newVal.length)
          for (let i = 0; i < max_len; i++) {
            const sub_path = `${path}[${i}]`
            if (i < oldVal.length && i < newVal.length) {
              this._deep_diff_into(oldVal[i], newVal[i], sub_path, changes)
            } else if (i < newVal.length) {
              changes.push({ type: 'added', path: sub_path, new: newVal[i] })
            } else {
              changes.push({ type: 'removed', path: sub_path, old: oldVal[i] })
            }
          }
        }
        return
      }

      changes.push({
        type: oldVal === undefined ? 'added' : (newVal === undefined ? 'removed' : 'changed'),
        path: path || '(root)',
        old: oldVal,
        new: newVal,
      })
    },
    detect_array_id_key(oldArr, newArr) {
      const candidates = ['eventName', 'yard_name', 'name', 'id']
      const all = [...oldArr, ...newArr]
      if (all.length === 0) return null
      if (!all.every(item => this.is_plain_object(item))) return null
      for (const key of candidates) {
        if (all.every(item => key in item && item[key] != null)) {
          return key
        }
      }
      return null
    },
    _diff_arrays_by_id(oldArr, newArr, path, id_key, changes) {
      const old_by_id = {}
      const new_by_id = {}
      for (const item of oldArr) old_by_id[String(item[id_key])] = item
      for (const item of newArr) new_by_id[String(item[id_key])] = item
      const ids = new Set([...Object.keys(old_by_id), ...Object.keys(new_by_id)])
      const sorted = [...ids].sort()
      for (const id of sorted) {
        const sub_path = `${path}[${id}]`
        const o = old_by_id[id]
        const n = new_by_id[id]
        if (o && n) {
          this._deep_diff_into(o, n, sub_path, changes)
        } else if (n) {
          changes.push({ type: 'added', path: sub_path, new: n })
        } else {
          changes.push({ type: 'removed', path: sub_path, old: o })
        }
      }
    },
    values_equal(a, b) {
      if (a === b) return true
      if (a == null || b == null) return a == null && b == null
      if (typeof a !== typeof b) {
        // Compare numeric strings vs numbers (DynamoDB Decimal can show up either way)
        if ((typeof a === 'number' || typeof a === 'string') && (typeof b === 'number' || typeof b === 'string')) {
          return String(a) === String(b)
        }
        return false
      }
      if (typeof a !== 'object') return false
      try {
        return JSON.stringify(a) === JSON.stringify(b)
      } catch (e) {
        return false
      }
    },
    is_plain_object(val) {
      return val !== null && typeof val === 'object' && !Array.isArray(val)
    },

    humanize_path(path) {
      if (!path) return '(root)'
      return path
        .split('.')
        .map(seg => seg
          .replace(/\[([^\]]+)\]/g, ' › $1')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase()))
        .join(' › ')
    },
    badge_label(type) {
      if (type === 'added') return 'Added'
      if (type === 'removed') return 'Removed'
      return 'Changed'
    },
    format_value(val) {
      if (val === undefined || val === null) return '(empty)'
      if (val === '') return '(empty)'
      if (typeof val === 'boolean') return val ? 'true' : 'false'
      if (typeof val === 'number') return String(val)
      if (typeof val === 'string') {
        if (val.length > 400) return val.slice(0, 400) + '…'
        return val
      }
      try {
        const s = JSON.stringify(val, null, 2)
        if (s.length > 1500) return s.slice(0, 1500) + '\n… (truncated, see raw JSON)'
        return s
      } catch (e) {
        return String(val)
      }
    },
    pretty_json(val) {
      try {
        return JSON.stringify(val, null, 2)
      } catch (e) {
        return String(val)
      }
    }

  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    is_admin() {
      return this.user.role === 'admin'
    },
    telematics_type() {
      return this.user.telematics_types[this.user.tenant]
    },
    tenant_type() {
      return this.user.types[this.user.tenant]
    }
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    text_filter() {
      this.filter_results()
    }
  }
}
</script>

<style>
.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }


div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
  /* overflow-wrap: break-word; */
}

recipients-list {
  white-space: break-spaces !important;
}

.diff-cell {
  text-align: left;
  font-size: 13px;
  line-height: 1.4;
  padding: 4px 2px;
}

.updated-by-username {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
  word-break: break-all;
}

.diff-summary {
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.diff-empty {
  color: #888;
  font-size: 13px;
}

.diff-event-msg {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
  padding: 6px 8px;
  word-break: break-word;
}

.diff-row {
  display: grid;
  grid-template-columns: 90px minmax(160px, 1fr) 3fr;
  gap: 8px;
  align-items: start;
  padding: 6px 6px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.diff-row + .diff-row {
  border-top: 1px solid #eee;
}

.diff-added { background-color: rgba(76, 175, 80, 0.06); }
.diff-removed { background-color: rgba(244, 67, 54, 0.06); }
.diff-changed { background-color: rgba(33, 150, 243, 0.04); }

.diff-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  text-align: center;
  white-space: nowrap;
  align-self: start;
}

.badge-added { background-color: #e8f5e9; color: #1b5e20; border: 1px solid #a5d6a7; }
.badge-removed { background-color: #ffebee; color: #b71c1c; border: 1px solid #ef9a9a; }
.badge-changed { background-color: #e3f2fd; color: #0d47a1; border: 1px solid #90caf9; }
.badge-event { background-color: #fff3e0; color: #e65100; border: 1px solid #ffcc80; }

.diff-field {
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.diff-values {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.diff-old, .diff-new {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 2px 6px;
  border-radius: 3px;
}

.diff-old {
  background-color: #fdecea;
  color: #8a1f15;
  text-decoration: none;
}

.diff-new {
  background-color: #e6f4ea;
  color: #1b5e20;
}

.diff-arrow {
  font-weight: 700;
  margin-right: 4px;
  opacity: 0.7;
}

.diff-more {
  margin-top: 6px;
  font-size: 12px;
}

.diff-raw-toggle {
  margin-top: 10px;
  font-size: 11px;
}

.diff-raw-toggle a {
  color: #666;
}

.diff-raw {
  margin-top: 6px;
  border-top: 1px dashed #ccc;
  padding-top: 6px;
}

.diff-raw-section {
  margin-bottom: 6px;
}

.diff-raw-label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}

.diff-raw pre {
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 11px;
  max-height: 240px;
  overflow: auto;
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .diff-row {
    grid-template-columns: 1fr;
  }
}

</style>

